import React, { FC, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { Link, Stack, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { AtsType } from 'src/generated/mloop-graphql';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import useSmartRecruitersIntegrationPut from 'src/hooks/api/integration/useSmartRecruitersIntegrationPut';
import { getAtsService } from 'src/hooks/atsService';

interface Props {
  onClose: () => void;
  onSave: () => void;
  active: boolean;
  apiKey?: string;
  disabled?: boolean;
}

const SmartRecruitersDialog: FC<Props> = ({ onClose, active, apiKey, onSave, disabled = false }) => {
  const { t } = useTranslation();
  const [apiKeyVal, setApiKeyVal] = useState(apiKey);
  const atsService = getAtsService(AtsType.SmartRecruiters);

  const apiKeyNotRequired = useFlag('smart_recruiters_new_sourcing_link_permissions');

  const putSmartRecruiters = useSmartRecruitersIntegrationPut();

  const onSubmit = () => {
    if (!apiKeyVal && apiKeyNotRequired) {
      onSave();
      return;
    }
    putSmartRecruiters.mutate(
      {
        apiKey: apiKeyVal,
        active,
      },
      {
        onSuccess: onSave,
        onError: (e) => {
          enqueueSnackbar(getExternalErrorMessage(e), { variant: 'error' });
        },
      }
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('hiring-dashboard-settings-integrations:settings.integrations.dialogTitle.smartRecruiters')}
      maxWidth="xs"
      submitOptions={{
        isDisabled: disabled || (apiKeyNotRequired ? false : !apiKeyVal),
        onClick: onSubmit,
        label: t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.continueButton'),
      }}
      cancelOptions={{
        label: t('hiring-dashboard-common:common.cancel'),
        onClick: () => onClose(),
      }}
      decoration={
        <img
          style={{ borderRadius: '6px' }}
          height="64px"
          width="64px"
          alt={t('hiring-dashboard-settings-integrations:settings.integrations.apiKeyDialog.logoAlt')}
          src={atsService.logoUrl}
        />
      }
    >
      <Stack spacing={1}>
        <Stack direction="column">
          <Typography variant="subtitle1">
            {t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.apiKeyTitle')}
            {apiKeyNotRequired ? (
              <Typography component="span" color="text.secondary">
                {' '}
                {t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.apiKeyOptional')}
              </Typography>
            ) : (
              ''
            )}
          </Typography>
          <Typography color="text.secondary">
            {t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.apiKeyDescription')}
            <Link
              target="_blank"
              href="https://www.smartrecruiters.com/settings/administration/app-management/custom-applications/credentials/create"
            >
              {' '}
              {t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.credentials')}
            </Link>
          </Typography>
        </Stack>

        <TextField
          value={apiKeyVal}
          placeholder={t(
            'hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.apiKeyPlaceholder'
          )}
          onChange={(e) => setApiKeyVal(e.target.value)}
          fullWidth
        />
        <Typography color="text.secondary" variant="body2">
          {t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.needHelp')}{' '}
          <Link target="_blank" href={HelpCenterUrl.SMART_RECRUITERS_INTEGRATION}>
            {t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.learnMore')}
          </Link>{' '}
          {t('hiring-dashboard-settings-integrations:settings.integrations.smartRecruitersDialog.aboutSmartRecruiters')}
        </Typography>
      </Stack>
    </Dialog>
  );
};

export default SmartRecruitersDialog;
